import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormHelperText, Grid, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { selectWhatsappState } from '../../state/whatsapp/whatsappSelector';
import { clearTemplateStatus, getTemplates, getTemplateStatus } from '../../state/whatsapp/whatsappSlice';
import { formatWhatsappPhone, getPreviewBody, getVariablesFromBody } from '../../helpers/utils/textFormatter';
import Styles from './whatsapp.module.scss'

const WhatsappTemplateModal = ({
    title = "Send Template Message to",
    open,
    selectedContact,
    isLoading,
    onSubmit = () => { },
    onChange = () => { },
    onClose = () => { }
}) => {
    const dispatch = useDispatch();
    const [selectedTemplateId, setSelectedTemplateId] = useState('');
    const [templateVariables, setTemplateVariables] = useState({});
    const [phoneNumber, setPhoneNumber] = useState(selectedContact?.phone || '');
    const [phoneError, setPhoneError] = useState('');
    const [isPhoneTouched, setIsPhoneTouched] = useState(false);
    const { templates, isTemplatesLoading, currentTemplateStatus, isTemplateStatusLoading } = useSelector(selectWhatsappState);

    useEffect(() => {
        dispatch(getTemplates());
    }, []);

    const validatePhoneNumber = (value) => {
        const digitsOnly = `${value || ''}`.replace(/\D/g, '');

        if (!digitsOnly) {
            return 'Phone number is required';
        }

        if (digitsOnly.length < 8 || digitsOnly.length > 15) {
            return 'Enter a valid phone number';
        }

        return '';
    };

    const handleChangeTemplateId = (sid) => {
        setSelectedTemplateId(sid);
        setTemplateVariables({});
        if (sid) {
            dispatch(getTemplateStatus({ sid }));
        } else {
            dispatch(clearTemplateStatus());
        }
        onChange(sid);
    };

    const handleCloseTemplateModal = () => {
        setSelectedTemplateId('');
        setTemplateVariables({});
        setPhoneNumber(selectedContact?.phone || '');
        setPhoneError('');
        setIsPhoneTouched(false);
        dispatch(clearTemplateStatus());
        onClose();
    };

    const handlePhoneChange = (value) => {
        setPhoneNumber(value);

        if (isPhoneTouched) {
            setPhoneError(validatePhoneNumber(value));
        }
    };

    const handlePhoneBlur = () => {
        setIsPhoneTouched(true);
        setPhoneError(validatePhoneNumber(phoneNumber));
    };

    const handleSubmitTemplate = () => {
        if (!selectedContact?.phone) {
            const validationMessage = validatePhoneNumber(phoneNumber);

            setIsPhoneTouched(true);
            setPhoneError(validationMessage);

            if (validationMessage) {
                return;
            }
        }

        onSubmit(templateVariables, selectedTemplateId, phoneNumber);
    };

    const activeTemplate = templates.find(t => t.sid === selectedTemplateId);
    const varKeys = activeTemplate
        ? (activeTemplate.variables && Object.keys(activeTemplate.variables).length > 0
            ? Object.keys(activeTemplate.variables).sort((a, b) => Number(a) - Number(b))
            : getVariablesFromBody(activeTemplate.body))
        : [];
    return (
        <Dialog open={open} onClose={handleCloseTemplateModal} maxWidth="md" fullWidth>
            <DialogTitle sx={{ borderBottom: '1px solid #e0e0e0', pb: 1.5 }}>{title} {formatWhatsappPhone(phoneNumber)}</DialogTitle>
            <DialogContent sx={{ p: 3, pt: '20px !important' }}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6} px={1.5}>
                        <Stack spacing={2.5}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="template-select-label">Select Template</InputLabel>
                                <Select
                                    labelId="template-select-label"
                                    value={selectedTemplateId}
                                    label="Select Template"
                                    onChange={(e) => handleChangeTemplateId(e.target.value)}
                                >
                                    {isTemplatesLoading ? (
                                        <MenuItem disabled>Loading templates...</MenuItem>
                                    ) : (
                                        templates.map((t) => (
                                            <MenuItem key={t.sid} value={t.sid}>
                                                {t.friendlyName || t.sid} ({t.language})
                                            </MenuItem>
                                        ))
                                    )}
                                </Select>
                            </FormControl>
                            {(selectedContact && selectedContact.phone) ? null : (
                                <FormControl fullWidth size="small" error={!!(isPhoneTouched && phoneError)}>
                                    <InputLabel shrink id="phone-number-label">Type Recipient's Phone Number</InputLabel>
                                    <Box
                                        sx={{
                                            '& .react-tel-input .selected-flag:hover, .react-tel-input .selected-flag:focus': {
                                                backgroundColor: 'transparent',
                                            },
                                        }}
                                    >
                                        <PhoneInput
                                            country={'us'}
                                            value={phoneNumber}
                                            onChange={handlePhoneChange}
                                            onBlur={handlePhoneBlur}
                                            enableSearch={true}
                                            inputStyle={{
                                                width: '100%',
                                                height: '40px',
                                                fontSize: '16px',
                                                borderRadius: '8px',
                                                border: `1px solid ${isPhoneTouched && phoneError ? '#d32f2f' : 'rgba(0, 0, 0, 0.23)'}`,
                                                padding: '10px 14px 10px 58px',
                                                backgroundColor: '#FAFAFA',
                                            }}
                                            buttonStyle={{
                                                backgroundColor: 'transparent',
                                                border: 'none',
                                                borderRadius: '8px 0 0 8px',
                                                height: '38px',
                                                top: '1px',
                                                left: '1px',
                                                width: '52px',
                                                borderRight: `1px solid ${isPhoneTouched && phoneError ? '#d32f2f' : 'rgba(0, 0, 0, 0.23)'}`,
                                            }}
                                            specialLabel={''}
                                        />
                                    </Box>
                                    {isPhoneTouched && phoneError && <FormHelperText error>{phoneError}</FormHelperText>}
                                </FormControl>
                            )}
                            {isTemplateStatusLoading ? (
                                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                                    <CircularProgress size={12} color="inherit" /> Checking template approval status...
                                </Typography>
                            ) : currentTemplateStatus && currentTemplateStatus !== 'approved' ? (
                                <Box sx={{ p: 1.5, bgcolor: '#fff5f5', borderLeft: '4px solid #d32f2f', borderRadius: '4px', mt: 1 }}>
                                    <Typography variant="body2" sx={{ color: '#d32f2f', fontWeight: 500 }}>
                                        Template cannot be used, status is {currentTemplateStatus}.
                                    </Typography>
                                </Box>
                            ) : null}

                            {activeTemplate && (
                                <Stack spacing={2}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Fill Template Variables
                                    </Typography>
                                    {varKeys.map((k) => (
                                        <TextField
                                            title={`Sample Data {{${k}}}: ${activeTemplate.variables?.[k] || ''}`}
                                            key={k}
                                            label={`Sample Data {{${k}}}: ${activeTemplate.variables?.[k] || ''}`}
                                            fullWidth
                                            size="small"
                                            value={templateVariables[k] || ''}
                                            onChange={(e) =>
                                                setTemplateVariables((prev) => ({
                                                    ...prev,
                                                    [k]: e.target.value,
                                                }))
                                            }
                                        />
                                    ))}
                                </Stack>
                            )}
                        </Stack>
                    </Grid>

                    {/* Right Side: Real-time Message Preview */}
                    <Grid item xs={12} md={6} sx={{ borderLeft: { md: '1px solid #e0e0e0' } }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', maxHeight: 400 }}>
                            <Typography variant="subtitle2" color="text.secondary" mb={1.5}>
                                Message Preview
                            </Typography>
                            {activeTemplate ? (
                                <Box
                                    sx={{
                                        flex: 1,
                                        backgroundColor: '#ece5dd',
                                        borderRadius: '8px',
                                        p: 2,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'flex-start',
                                        alignItems: 'flex-end',
                                        overflowY: 'auto',
                                        height: '100%',
                                        maxHeight: 400,
                                    }}
                                >
                                    <Box
                                        className={Styles.messageBubble}
                                        sx={{
                                            bgcolor: '#dcf8c6',
                                            alignSelf: 'flex-start',
                                            maxWidth: '90%',
                                            mb: 0,
                                            borderRadius: '8px',
                                            borderTopRightRadius: '8px',
                                            p: '8px 12px'
                                        }}
                                    >
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                                            {formatWhatsappPhone(getPreviewBody(activeTemplate.body, templateVariables))}
                                        </Typography>
                                    </Box>
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        flex: 1,
                                        backgroundColor: '#f8f9fa',
                                        borderRadius: '8px',
                                        border: '1px dashed #bdbdbd',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        p: 3,
                                        textAlign: 'center'
                                    }}
                                >
                                    <Typography variant="body2" color="text.secondary">
                                        Select a template to view the preview
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, borderTop: '1px solid #e0e0e0', pt: 1.5 }}>
                <Button onClick={handleCloseTemplateModal}>Close</Button>
                <Button
                    variant="contained"
                    disabled={
                        !selectedTemplateId ||
                        varKeys.some((v) => !templateVariables[v]?.trim()) ||
                        isLoading ||
                        isTemplateStatusLoading ||
                        (currentTemplateStatus && currentTemplateStatus !== 'approved') ||
                        (!selectedContact?.phone && !!validatePhoneNumber(phoneNumber))
                    }
                    onClick={handleSubmitTemplate}
                    sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#128C7E' } }}
                >
                    {isLoading ? <CircularProgress size={20} /> : 'Send Template'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default WhatsappTemplateModal
